import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';

// MUI Imports
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete'; // Import DeleteIcon
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
// Imports for Dialog
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Slider from '@mui/material/Slider';
import FormGroup from '@mui/material/FormGroup';
import Divider from '@mui/material/Divider';
import FormLabel from '@mui/material/FormLabel'; // For slider label
import SpeechToText from '../SpeechToText';

function JournalEntryEditor({ token }) { // Assume token is passed as a prop
  const { entryId } = useParams(); // Get entryId from URL if present
  const navigate = useNavigate();
  const isNewEntry = !entryId;

  // Existing State
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [entryDate, setEntryDate] = useState(null); // To display date for existing entries
  const [loading, setLoading] = useState(!isNewEntry); // Load if editing existing
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false); // State for delete confirmation dialog

  // New State for Goal Linking & Habit Tracking
  const [allGoals, setAllGoals] = useState([]); // Store ALL user goals
  const [goalsForDropdown, setGoalsForDropdown] = useState([]); // Filtered goals + potentially the selected one
  const [selectedGoalId, setSelectedGoalId] = useState(''); // ID of the linked goal
  const [isHabitGoalSelected, setIsHabitGoalSelected] = useState(false); // Track if selected goal is a habit
  // const [behaviourTracked, setBehaviourTracked] = useState(false); // REMOVED - Now derived from isHabitGoalSelected
  const [completionStatus, setCompletionStatus] = useState(''); // Dropdown state ('', 'completed', etc.)
  const [effortRating, setEffortRating] = useState(null); // Slider state (null or 1-5)

  // Refs for focusing after speech input
  const titleRef = useRef(null);
  const contentRef = useRef(null);

  // Fetch ALL goals
  const fetchAllGoals = useCallback(async () => {
    if (!token) return;
    try {
      const fetchedGoals = await api.getGoals(token);
      setAllGoals(fetchedGoals); // Store all goals
    } catch (err) {
      console.error("Error fetching all goals:", err);
      // Non-critical error, maybe show a small warning?
      setError(prev => prev ? `${prev}\nCould not load goals.` : 'Could not load goals.');
    }
  }, [token]);

  // Fetch existing entry data if entryId is present
  const fetchEntry = useCallback(async () => {
    if (isNewEntry || !token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const entry = await api.getJournalEntryById(entryId, token);
      setTitle(entry.title || '');
      setContent(entry.content);
      setEntryDate(entry.date); // Store the date

      // Populate new fields if they exist on the entry
      setSelectedGoalId(entry.relatedGoal || '');
      // setBehaviourTracked(entry.behaviourTracked || false); // REMOVED
      setCompletionStatus(entry.completionStatus || '');
      setEffortRating(entry.effortRating || null);

      // Determine if the loaded goal is a habit goal (requires allGoals to be loaded)
      // We'll handle this in a separate effect after goals are loaded

    } catch (err) {
      console.error("Error fetching journal entry:", err);
      setError(err.message || 'Failed to load journal entry.');
    } finally {
      setLoading(false);
    }
  }, [entryId, isNewEntry, token]);

  // Initial data fetching: Fetch goals first, then entry if editing
  useEffect(() => {
    fetchAllGoals();
    if (!isNewEntry) {
      fetchEntry();
    }
  }, [fetchAllGoals, fetchEntry, isNewEntry]);


  // Effect to update the dropdown list and determine selected goal type
  useEffect(() => {
    // Filter goals for dropdown (active or on hold)
    const activeAndOnHoldGoals = allGoals.filter(goal => goal.status === 'active' || goal.status === 'on_hold');

    let currentSelectedGoal = null;
    if (selectedGoalId) {
      currentSelectedGoal = allGoals.find(g => g._id === selectedGoalId);
    }

    // Determine if the selected goal is a habit type
    setIsHabitGoalSelected(currentSelectedGoal?.goalType === 'habit');

    // Ensure the currently selected goal is in the dropdown list, even if completed
    let finalDropdownList = [...activeAndOnHoldGoals];
    if (currentSelectedGoal && !activeAndOnHoldGoals.some(g => g._id === selectedGoalId)) {
      finalDropdownList.push(currentSelectedGoal);
      // Optional: Sort the list again if needed, e.g., by description or status
      finalDropdownList.sort((a, b) => a.description.localeCompare(b.description));
    }

    setGoalsForDropdown(finalDropdownList);

  }, [selectedGoalId, allGoals]); // Rerun when selection or the full goal list changes

  // Reset tracking fields if the selected goal changes or is deselected, or if it's not a habit goal.
  useEffect(() => {
      if (!selectedGoalId || !isHabitGoalSelected) {
          // setBehaviourTracked(false); // REMOVED
          setCompletionStatus('');
          setEffortRating(null);
      }
      // If a habit goal IS selected, ensure completionStatus is at least an empty string
      // (or default to 'attempted'?) Let's keep it empty for now.
      // else if (isHabitGoalSelected && !completionStatus) {
      //    setCompletionStatus(''); // Or 'attempted'?
      // }
  }, [selectedGoalId, isHabitGoalSelected]);


  const handleSave = async () => {
    if (!content.trim()) {
      setError('Journal content cannot be empty.');
      return;
    }
    if (!token) {
      setError('Authentication token is missing.');
      return;
    }

    setSaving(true);
    setError('');
    setSaveSuccess(false);

    // --- MORE DEBUGGING ---
    console.log('[JournalEntryEditor] handleSave Start:');
    console.log('  - selectedGoalId:', selectedGoalId);
    console.log('  - allGoals available:', allGoals.length > 0 ? 'Yes' : 'No');

    // Determine if the currently selected goal is a habit directly within handleSave
    // Use allGoals here as the selected goal might be completed and not in the filtered list
    let isSelectedGoalHabitType = false;
    let foundGoal = null;
    if (selectedGoalId) {
        foundGoal = allGoals.find(g => g._id === selectedGoalId); // Search in allGoals
        isSelectedGoalHabitType = foundGoal?.goalType === 'habit';
        console.log(`  - Goal search result (from allGoals): Found=${!!foundGoal}, Type=${foundGoal?.goalType}, isHabitCalc=${isSelectedGoalHabitType}`);
    } else {
         console.log('  - No goal selected.');
    }
    // --- END MORE DEBUGGING ---

    // Construct entry data conditionally based on goal selection and type
    const entryData = {
      title: title.trim() || null,
      content: content.trim(),
      relatedGoal: selectedGoalId || null, // Send null if empty string
    };

    // Add goal-related fields ONLY if a goal is selected
    if (foundGoal) {
      // behaviourTracked: true for habit, false for task
      entryData.behaviourTracked = foundGoal.goalType === 'habit';

      // completionStatus: Add if a status is selected (for any goal type)
      if (completionStatus) {
        entryData.completionStatus = completionStatus;
      } else {
         entryData.completionStatus = null; // Explicitly send null if selected but then cleared
      }

      // effortRating: Add ONLY if it's a habit goal AND a rating is selected
      if (foundGoal.goalType === 'habit' && effortRating) {
        entryData.effortRating = effortRating;
      } else if (foundGoal.goalType === 'habit') {
          entryData.effortRating = null; // Explicitly send null if habit and cleared
      }
       // No need to add effortRating if it's a task goal
    }
    // No cleanup block needed as fields are added conditionally

     console.log('  - Constructed entryData (final):', JSON.stringify(entryData, null, 2));

    try {
      if (isNewEntry) {
        await api.createJournalEntry(entryData, token);
        setSaveSuccess(true);
        setTimeout(() => navigate('/journal'), 1500); // Navigate back to list after success
      } else {
        // Implement update functionality
        await api.updateJournalEntry(entryId, entryData, token);
        setSaveSuccess(true);
         // Optionally navigate back or just show success
         // setTimeout(() => navigate('/journal'), 1500);
      }
    } catch (err) {
      console.error("Error saving journal entry:", err);
      setError(err.message || 'Failed to save journal entry.');
      setSaveSuccess(false);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/journal'); // Navigate back to the journal list
  };

  // --- Delete Logic ---
  const handleDeleteClick = () => {
    setDeleteConfirmOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
  };

  const handleDeleteConfirm = async () => {
    setDeleteConfirmOpen(false);
    if (isNewEntry || !token) {
      setError('Cannot delete an unsaved entry or missing token.');
      return;
    }
    setSaving(true); // Use saving state to disable buttons during delete
    setError('');
    try {
      await api.deleteJournalEntry(entryId, token);
      // Optionally show a success message before navigating
      // setSaveSuccess(true); // Maybe reuse success alert? Or a dedicated delete success state?
      console.log('Journal entry deleted successfully.');
      navigate('/journal'); // Navigate back to list after successful delete
    } catch (err) {
      console.error("Error deleting journal entry:", err);
      setError(err.message || 'Failed to delete journal entry.');
      setSaving(false); // Re-enable buttons on error
    }
    // No finally block needed here as navigation happens on success
  };
  // --- End Delete Logic ---

  // Handle slider change - ensure value is number or null
  const handleSliderChange = (event, newValue) => {
    setEffortRating(newValue === 0 ? null : newValue); // Treat 0 as null/unset? Or keep 0? Let's allow 1-5.
     // Slider gives a number, directly set it.
     setEffortRating(newValue);
  };

  // Speech-to-Text handlers
  const handleTitleSpeechTranscript = (transcript) => {
    if (transcript.trim()) {
      setTitle((prevTitle) => {
        const newTitle = prevTitle
          ? `${prevTitle} ${transcript.trim()}`
          : transcript.trim();
        return newTitle;
      });

      // Focus the title field after adding speech text
      setTimeout(() => {
        if (titleRef.current) {
          titleRef.current.focus();
          const length = titleRef.current.value.length;
          titleRef.current.setSelectionRange(length, length);
        }
      }, 100);
    }
  };

  const handleContentSpeechTranscript = (transcript) => {
    if (transcript.trim()) {
      setContent((prevContent) => {
        const newContent = prevContent
          ? `${prevContent} ${transcript.trim()}`
          : transcript.trim();
        return newContent;
      });

      // Focus the content field after adding speech text
      setTimeout(() => {
        if (contentRef.current) {
          contentRef.current.focus();
          const length = contentRef.current.value.length;
          contentRef.current.setSelectionRange(length, length);
        }
      }, 100);
    }
  };

  if (loading && !isNewEntry) {
    // Only show loading indicator when fetching existing entry
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, margin: 'auto', mt: 4, p: 2 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back to Journal List
        </Button>
        <Typography variant="h5" component="h1" gutterBottom>
          {isNewEntry ? 'New Journal Entry' : 'View/Edit Journal Entry'}
        </Typography>
        {!isNewEntry && entryDate && (
          <Typography variant="caption" display="block" color="text.secondary" gutterBottom>
            Written on: {new Date(entryDate).toLocaleString()}
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}
        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>Journal entry saved successfully!</Alert>
        )}

        {/* Title Field with Speech-to-Text */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography variant="subtitle1">Title (Optional)</Typography>
            <SpeechToText
              onTranscript={handleTitleSpeechTranscript}
              disabled={saving}
              language="en-US"
              continuous={false}
              interimResults={true}
              showInterimText={true}
            />
          </Box>
          <TextField
            inputRef={titleRef}
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            margin="normal"
            disabled={saving}
            sx={{ mt: 0 }}
          />
        </Box>

        {/* Content Field with Speech-to-Text */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography variant="subtitle1">Content *</Typography>
            <SpeechToText
              onTranscript={handleContentSpeechTranscript}
              disabled={saving}
              language="en-US"
              continuous={false}
              interimResults={true}
              showInterimText={true}
            />
          </Box>
          <TextField
            inputRef={contentRef}
            fullWidth
            multiline
            rows={10}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            margin="normal"
            required
            disabled={saving}
            placeholder="Write your thoughts here..."
            sx={{ mt: 0 }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Goal Linking and Habit Tracking Section */}
        <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>Track Goal Progress (Optional)</Typography>

        <FormControl fullWidth margin="normal" size="small">
          <InputLabel id="related-goal-label">Link to Goal</InputLabel>
          <Select
            labelId="related-goal-label"
            value={selectedGoalId}
            label="Link to Goal"
            onChange={(e) => setSelectedGoalId(e.target.value)}
            disabled={saving}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {goalsForDropdown.map((goal) => (
              <MenuItem key={goal._id} value={goal._id}>
                 ({goal.goalType === 'habit' ? 'Habit' : 'Task'}) {goal.description} {goal.status !== 'active' ? `[${goal.status}]` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Show Completion Status if ANY goal is selected */}
        {selectedGoalId && (
           <FormControl fullWidth margin="dense" size="small" sx={{ mt: 1 }}> {/* Add some top margin */}
              <InputLabel id="completion-status-label">Completion Status</InputLabel>
              <Select
                      labelId="completion-status-label"
                      value={completionStatus}
                      label="Completion"
                      onChange={(e) => setCompletionStatus(e.target.value)}
                      disabled={saving}
                    >
                 <MenuItem value=""><em>Not Specified</em></MenuItem>
                 <MenuItem value="completed">Completed</MenuItem>
                 <MenuItem value="partially_completed">Partially Completed</MenuItem>
                 <MenuItem value="started">Started</MenuItem> {/* Added Started for tasks */}
                 <MenuItem value="worked_on">Worked On</MenuItem> {/* Added Worked On for tasks */}
                 <MenuItem value="missed">Missed</MenuItem> {/* Keep Missed */}
                 <MenuItem value="skipped">Skipped</MenuItem> {/* Keep Skipped */}
              </Select>
           </FormControl>
        )}

        {/* Conditional Habit Tracking Fields - Effort only for habits */}
        {/* Conditional Habit Tracking Fields - Effort only for habits */}
        {isHabitGoalSelected && selectedGoalId && ( // Ensure selectedGoalId is also present
          <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
             <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                {/* Find goal from allGoals to display description */}
                Tracking habit: "{allGoals.find(g => g._id === selectedGoalId)?.description || 'Unknown Habit'}"
             </Typography>
             <Box sx={{ mt: 1 }}> {/* Removed indentation */}
                 {/* Completion Status moved outside */}
                <FormControl fullWidth margin="dense">
                   <FormLabel component="legend" sx={{ fontSize: '0.8rem', mb: -0.5 }}>Effort / Quality (Optional, 1-5)</FormLabel>
                   <Slider
                        aria-label="Effort Rating"
                        value={effortRating || 0} // Slider needs a number, default to 0 if null
                        onChange={handleSliderChange}
                        valueLabelDisplay="auto"
                        step={1}
                        marks={[
                            { value: 1, label: '1' },
                            { value: 2, label: '2' },
                            { value: 3, label: '3' },
                            { value: 4, label: '4' },
                            { value: 5, label: '5' },
                        ]}
                       min={1}
                       max={5}
                       disabled={saving}
                       size="small"
                   />
                </FormControl>
             </Box>
          </Paper>
        )}


        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}> {/* Changed to space-between */}
          {/* Delete Button - Only show for existing entries */}
          {!isNewEntry && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteClick}
              disabled={saving} // Disable while saving/deleting
            >
              Delete
            </Button>
          )}
          {/* Spacer if delete button isn't shown */}
          {isNewEntry && <Box sx={{ flexGrow: 1 }} />}

          {/* Save Button */}
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || !content.trim()} // Disable if saving or content is empty
          >
            {saving ? 'Saving...' : (isNewEntry ? 'Save Entry' : 'Save Changes')}
          </Button>
        </Box>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Deletion"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this journal entry? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default JournalEntryEditor;
